import {useEffect, useState, useMemo} from 'react';
import {ChevronRightIcon, TrashIcon} from '@heroicons/react/24/outline';
import {useIntl} from 'react-intl';

import {ExtrasHTTP} from '../../../providers/extras';
import {useJobLocation} from '../../../hooks/useJobLocation';
import {useEmployee} from '../../../hooks/useEmployee';

import {currencyFormat} from '../../../../../utils/currency';
import pictureImage from '../../../../../assets/images/picture.png';
import pdfImage from '../../../../../assets/images/pdf.png';

import ConfirmationModal from '../../../../../components/_modals/ConfirmationModal';
import SectionWrapper from '../../../../../components/SectionWrapper';
import {FormatDate} from '../../../../../components/FormatDate';
import PageHeader from '../../../../../components/_layouts/PageHeader';
import IconButton from '../../../../../components/IconButton';
import SearchSelect from '../../../../../components/_form/SearchSelect';
import Table from '../../../../../components/Table';
import Badge from '../../../../../components/Badge';
import PreviewModal from '../../../../../components/_modals/PreviewModal';
import Pagination from '../../../../../components/Pagination';
import FormModal from './components/Form';

const getFileImage = (file, company_id = '') => {
  if (!file) return pictureImage;

  if (file.includes('.pdf')) return pdfImage;

  if (!file.includes('storage.googleapis')) {
    return `${process.env.REACT_APP_API_URL}/files?model=expenses&company=${company_id}&file=${file}`;
  }

  return file;
};

const EmployeesReceipts = () => {
  const {formatMessage} = useIntl();
  const {jobs, getJobs} = useJobLocation();
  const {getEmployees, employees} = useEmployee();

  const [categories, setCategories] = useState({
    loading: true,
    data: [],
  });
  const [extras, setExtras] = useState({
    loading: true,
    data: [],
  });
  const [previewFile, setPreviewFile] = useState({
    open: false,
    file: '',
  });
  const [modal, setModal] = useState({
    open: false,
    type: 'create',
    data: {},
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({category: '', work: ''});
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    id: null,
  });

  const handleOpenPreview = (file, company_id) => {
    const fileUrl =
      !file.includes('trackmyguys.com/storage') &&
      !file.includes('storage.googleapis')
        ? `${process.env.REACT_APP_API_URL}/files?model=expenses&company=${company_id}&file=${file}`
        : file;

    if (file.includes('.pdf')) return window.open(fileUrl, '_blank');

    return setPreviewFile({open: true, file: fileUrl});
  };

  const columns = useMemo(
    () => [
      {
        Header: formatMessage({id: 'preview'}),
        accessor: ({file, company_id}) => (
          <button
            className="border-0 bg-transparent"
            onClick={() => handleOpenPreview(file, company_id)}
            type="button">
            <img
              src={getFileImage(file, company_id)}
              alt="Receipt"
              width="70"
              loading="lazy"
            />
          </button>
        ),
      },
      {
        Header: formatMessage({id: 'employee'}),
        accessor: 'user_id.name',
      },
      {
        Header: formatMessage({id: 'category-description'}),
        accessor: 'description',
        Cell: ({row}) => (
          <div>
            <Badge variant="info">
              {row.original.category_id?.name ?? 'No category'}
            </Badge>
            <p className="w-full max-w-lg whitespace-normal text-xs mb-1">
              {row.original.description}
            </p>
          </div>
        ),
      },
      {
        Header: formatMessage({id: 'job-location'}),
        // eslint-disable-next-line react/destructuring-assignment
        accessor: ({work_id}) => (
          <Badge variant="info">{work_id?.name ?? 'No Job location'}</Badge>
        ),
      },
      {
        Header: formatMessage({id: 'amount'}),
        accessor: ({amount}) => currencyFormat(amount?.$numberDecimal),
      },
      {
        Header: formatMessage({id: 'date'}),
        accessor: 'send_in',
        Cell: ({value}) => <FormatDate value={value} pattern="P" />,
      },
      {
        Header: formatMessage({id: 'actions'}),
        accessor: '_id',
        Cell: ({row}) => (
          <div className="flex gap-2">
            <IconButton
              icon={TrashIcon}
              onClick={() =>
                setDeleteModal({open: true, id: row.original?._id})
              }
            />
            <IconButton
              icon={ChevronRightIcon}
              onClick={() =>
                setModal({type: 'edit', open: true, data: row.original})
              }
            />
          </div>
        ),
      },
    ],
    [formatMessage],
  );

  const fetchExtras = async (doLoading = true) => {
    if (doLoading && !extras.loading) setExtras({loading: true, data: []});

    const {data} = await ExtrasHTTP.getEmployeesReceipts({
      page: currentPage,
      ...filters,
    });

    setExtras({
      loading: false,
      data,
    });
  };

  const handleFilters = (type, selected) => {
    if (selected && selected.value) {
      setFilters((prev) => ({...prev, [type]: selected.value}));
    } else {
      setFilters((prev) => ({...prev, [type]: ''}));
    }
  };

  const handleDelete = async () => {
    try {
      await ExtrasHTTP.deleteEmployeesReceipts(deleteModal.id);
      await fetchExtras(false);
      setDeleteModal({open: false, id: null});
    } catch {
      throw new Error();
    }
  };

  useEffect(() => {
    fetchExtras();
  }, [currentPage, filters]);

  useEffect(() => {
    async function getCategories() {
      const {data} = await ExtrasHTTP.getCategories({status: 'active'});
      setCategories({loading: false, data});
    }

    getJobs();
    getCategories();
    getEmployees();
  }, []);

  return (
    <>
      <PageHeader
        title={formatMessage({id: 'menu.extras.employees-receipts'})}
        breadcrumbs={[
          {
            id: 'extras',
            label: formatMessage({id: 'extras'}),
          },
          {
            id: 'receipts',
            label: formatMessage({id: 'menu.extras.employees-receipts'}),
          },
        ]}
      />

      <SectionWrapper
        isLoading={extras.loading}
        toolbar={
          <>
            <div style={{width: 330}}>
              <SearchSelect
                placeholder={formatMessage({id: 'filter.job-location'})}
                options={jobs.data.map((j) => ({
                  value: j._id,
                  label: j.name,
                }))}
                onChange={(selected) => handleFilters('work', selected)}
              />
            </div>
            <div style={{width: 300}}>
              <SearchSelect
                placeholder={formatMessage({id: 'filter.category'})}
                options={categories.data.map((j) => ({
                  value: j._id,
                  label: j.name,
                }))}
                onChange={(selected) => handleFilters('category', selected)}
              />
            </div>
            <div style={{width: 300}}>
              {!employees.loading && (
                <SearchSelect
                  placeholder={formatMessage({id: 'filter.employee'})}
                  options={employees.all.map((e) => ({
                    value: e._id,
                    label: `${e.name} ${
                      e.status === 'inactive' ? '(inactive)' : ''
                    }`,
                  }))}
                  onChange={(selected) => handleFilters('user', selected)}
                />
              )}
            </div>
          </>
        }>
        <Table columns={columns} data={extras.data.docs} />

        <Pagination
          totalPages={extras.data.totalPages}
          currentPage={currentPage}
          totalRecords={extras.data.totalDocs}
          onChangePage={(page) => setCurrentPage(page)}
        />
      </SectionWrapper>

      <PreviewModal
        file={previewFile.file}
        open={previewFile.open}
        onClose={() => setPreviewFile({open: false, file: ''})}
      />

      <FormModal
        open={modal.open}
        onClose={() => setModal((prev) => ({...prev, open: false}))}
        onSuccess={() => {
          fetchExtras(!modal.data);
          setModal((prev) => ({...prev, open: false}));
        }}
        id={modal.data?._id}
        initialData={
          modal.data && modal.type === 'edit'
            ? {
                amount: modal.data?.amount?.$numberDecimal,
                category_id: modal.data?.category_id?._id,
                work_id: modal.data?.work_id,
                description: modal.data?.description,
                send_in: new Date(modal.data?.send_in),
                file: modal.data?.file,
              }
            : {}
        }
      />

      <ConfirmationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({open: false, id: null})}
        onSubmit={handleDelete}
        title={formatMessage({id: 'extra.delete-receipt'})}
      />
    </>
  );
};

export default EmployeesReceipts;
